import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { capitalize } from '@material-ui/core';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiTooltip from '@material-ui/core/Tooltip';


import { makeStyles, withStyles } from '@material-ui/core/styles';
import { component } from 'Helpers/PropTypes';
import { CircularProgress } from '@mui/material';

const MUI_COLORS = ['default', 'inherit', 'primary', 'secondary'];

export const COLORS = [
  ...MUI_COLORS,
  'accentBlue',
  'accentBlue2',
  'accentGray',
  'lightGray',
  'mediumGray',
  'primaryGray',
  'actionActive',
  'darkGray',
  'red',
  'white',
  'accentGreen',
  'orange',
  'success',
  'naturalGreen',
  'black',
];

const MUI_SIZES = ['small', 'medium'];

export const SIZES = [
  'extraSmall',
  ...MUI_SIZES,
  'large',
];

export const VARIANTS = ['normal', 'outlined'];

const getRootColor = (color) => {
  return {
    color,
    '&:hover': {
      color,
    },
  };
};

const getOutline = (color) => {
  return {
    border: `1px solid ${color}`,
    padding: '6px',
  };
};

const getIconSize = (fontSize) => {
  return {
    fontSize,
  };
};

const useCustomStyles = makeStyles(theme => ({
  iconSizeExtraSmall: getIconSize(theme.typography.pxToRem(14)),
  iconSizeSmall: getIconSize(theme.typography.pxToRem(20)),
  iconSizeMedium: {}, // Default size from MUI
  iconSizeLarge: getIconSize(theme.typography.pxToRem(35)),
  rootColorPrimary: getRootColor(theme.palette.primary.main),
  rootColorSecondary: getRootColor(theme.palette.secondary.main),
  rootColorAccentBlue: getRootColor(theme.palette.facet.accentBlue),
  rootColorAccentBlue2: getRootColor(theme.palette.facet.accentBlue2),
  rootColorAccentGray: getRootColor(theme.palette.facet.accentGray),
  rootColorDarkGray: getRootColor(theme.palette.facet.darkGray),
  rootColorLightGray: getRootColor(theme.palette.facet.lightGray),
  rootColorMediumGray: getRootColor(theme.palette.facet.mediumGray),
  rootColorPrimaryGray: getRootColor(theme.palette.facet.primaryGray),
  rootColorActionActive: getRootColor(theme.palette.facet.actionActive),
  rootColorRed: getRootColor(theme.palette.facet.red),
  rootOutlineRed: getOutline(theme.palette.facet.red),
  rootColorOrange: getRootColor(theme.palette.facet.orange),
  rootColorBlack: getRootColor(theme.palette.common.black),
  rootColorSeafoam: getRootColor(theme.palette.facet.seafoam),
  rootColorAccentGreen: getRootColor(theme.palette.facet.accentGreen),
  rootColorWhite: getRootColor(theme.palette.common.white),
  rootColorSuccess: getRootColor(theme.palette.success.main),
  rootColorNaturalGreen: getRootColor(theme.palette.facet.naturalGreen),
}));

const styles = () => ({
  tooltip: {},
  root: {},
  icon: {},
});

const getSizeProp = (size) => {
  if (MUI_SIZES.includes(size)) {
    return size;
  }

  return 'medium';
};

const IconButton = React.forwardRef(
  ({
    className,
    classes,
    color,
    title,
    icon,
    size,
    disabled,
    showTooltipOnDisabled,
    IconProps,
    loading,
    children,
    variant,
    ...rest
  }, ref) => {
    const customClasses = useCustomStyles();
    const doNotShowTooltip = !title || (disabled && !showTooltipOnDisabled);
    const IconComponent = icon;

    const iconButton = (
      <MuiIconButton
        classes={{
          root: classNames(classes.root, className, customClasses[`rootColor${capitalize(color)}`], variant === 'outlined' ? customClasses[`rootOutline${capitalize(color)}`] : ''),
        }}
        ref={ref}
        disabled={disabled}
        size={getSizeProp(size)}
        {...rest}
      >
        {
          loading ? <CircularProgress size={16} /> : (
            <IconComponent
              className={classNames(classes.icon, customClasses[`iconSize${capitalize(size)}`])}
              {...IconProps}
            />
          )
        }
        {children}
      </MuiIconButton>
    );

    if (doNotShowTooltip) {
      return iconButton;
    }

    return (
      <MuiTooltip
        className={classes.tooltip}
        title={title}
      >
        {/* This needs to be here so the tooltip can exist when the button is disabled */}
        <span>
          {iconButton}
        </span>
      </MuiTooltip>
    );
  },
);

IconButton.defaultProps = {
  className: '',
  color: 'primary',
  size: 'medium',
  title: null,
  disabled: false,
  IconProps: {},
  showTooltipOnDisabled: false,
  loading: false,
  variant: 'normal',
};

IconButton.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  disabled: PropTypes.bool,
  showTooltipOnDisabled: PropTypes.bool,
  title: PropTypes.string,
  icon: component.isRequired,
  variant: PropTypes.oneOf(VARIANTS),
  size: PropTypes.oneOf(SIZES),
  IconProps: PropTypes.object,
  loading: PropTypes.bool,
};

export default withStyles(styles)(IconButton);
